import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { CKEditor } from 'ckeditor4-react';
import { DatePicker, LocalizationProvider } from '@material-ui/pickers';
import AdapterDateFns from '@material-ui/pickers/adapter/date-fns';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { fetchPopupIdRequest } from '@redux/Popup/action';
import baseApi from '@redux/Popup/api';
import { configCkeditor } from '@configs/plugins/default';
import CountryTargeting from '@components/common/CountryTargeting/CountryTargeting';
import { DEFAULT_POPUP_TYPES } from '@constants/popupTypes';
import './styles.css';

const emptySchedule = (prefix) => ({
  [`${prefix}Time`]: '',
  [`${prefix}Date`]: ''
});

const CkeditorComp = ({
  title,
  content: description,
  settingData,
  isHeader,
  isCenter,
  popupType: defaultPopupType
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { popupInfo } = useSelector((state) => state.toPopup);
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id') || '';
  const queryPopupType = searchParams.get('type') || '';
  const currentDate = moment().format('MMM DD YYYY');
  const currentTime = moment().format('HH:mm');

  const [inputValue, setInputValue] = useState('<p></p>');
  const [inpTitle, setInpTitle] = useState('');
  const [inpUrl, setInpUrl] = useState('');
  const [startDateCheck, setStartDateCheck] = useState(true);
  const [endDateCheck, setEndDateCheck] = useState(true);
  const [startTime, setStartTime] = useState({ startTime: currentTime, startDate: currentDate });
  const [endTime, setEndTime] = useState({ endTime: currentTime, endDate: currentDate });
  const [hexColor, setHexColor] = useState('#41B60B');
  const [size, setSize] = useState({});
  const [contentMode, setContentMode] = useState('normal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [popupTypes, setPopupTypes] = useState([]);
  const [popupType, setPopupType] = useState(defaultPopupType || queryPopupType || '');
  const [newPopupType, setNewPopupType] = useState('');
  const [popupTypeError, setPopupTypeError] = useState('');
  const [countryTargeting, setCountryTargeting] = useState({
    mode: 'all',
    countryCodes: []
  });
  const [countryTargetingError, setCountryTargetingError] = useState('');

  const characterCount = inpTitle.length;
  const isHeaderType = popupType === 'Headline' || isHeader === true;
  const isCenterType = popupType === 'Center' || isCenter === true;

  useEffect(() => {
    if (id) dispatch(fetchPopupIdRequest({ params: { id } }));
  }, [dispatch, id]);

  useEffect(() => {
    let active = true;

    baseApi.fetchPopupTypes()
      .then((response) => {
        if (!active) return;
        const responseTypes = response?.data || [];
        const types = responseTypes.length > 0 ? responseTypes : DEFAULT_POPUP_TYPES;
        setPopupTypes(types);
        setPopupType((current) => current || defaultPopupType || queryPopupType || types[0]?.name || 'Center');
        setPopupTypeError('');
      })
      .catch(() => {
        if (!active) return;
        setPopupTypes(DEFAULT_POPUP_TYPES);
        setPopupType((current) => current || defaultPopupType || queryPopupType || 'Center');
        setPopupTypeError('');
      });

    return () => {
      active = false;
    };
  }, [defaultPopupType, queryPopupType]);

  useEffect(() => {
    if (!id || !popupInfo || Object.keys(popupInfo).length === 0) return;

    setInpTitle(popupInfo.title || '');
    setInpUrl(popupInfo.url || '');
    setInputValue(popupInfo.content || '<p></p>');
    setStartDateCheck(Boolean(popupInfo.startDate?.startTime));
    setEndDateCheck(Boolean(popupInfo.endDate?.endTime));
    setStartTime(popupInfo.startDate || emptySchedule('start'));
    setEndTime(popupInfo.endDate || emptySchedule('end'));
    setSize(popupInfo.size || {});
    setHexColor(popupInfo.headerColor || '#41B60B');
    setPopupType(popupInfo.popupPosition || defaultPopupType || queryPopupType || 'Center');

    const storedCountryCodes = popupInfo.countryTargeting?.countryCodes
      || popupInfo.targetCountries
      || popupInfo.countries
      || [];
    setCountryTargeting({
      mode: popupInfo.countryTargeting?.mode || (storedCountryCodes.length > 0 ? 'include' : 'all'),
      countryCodes: storedCountryCodes
    });
  }, [id, popupInfo, defaultPopupType, queryPopupType]);

  const bodyForm = useMemo(() => ({
    popupPosition: popupType || defaultPopupType || queryPopupType || 'Center',
    title: inpTitle.trim(),
    clicked: 0,
    startDate: startDateCheck ? startTime : emptySchedule('start'),
    endDate: endDateCheck ? endTime : emptySchedule('end'),
    headerColor: hexColor,
    content: inputValue,
    url: inpUrl.trim().replace(/^(http:|https:)/i, 'https:'),
    size,
    countryTargeting: {
      mode: countryTargeting.mode,
      countryCodes: countryTargeting.mode === 'all' ? [] : countryTargeting.countryCodes
    }
  }), [
    popupType,
    defaultPopupType,
    queryPopupType,
    inpTitle,
    startDateCheck,
    startTime,
    endDateCheck,
    endTime,
    hexColor,
    inputValue,
    inpUrl,
    size,
    countryTargeting
  ]);

  const handleAddPopupType = async () => {
    const label = newPopupType.trim();
    if (!label) {
      setPopupTypeError('Vui lòng nhập tên loại popup mới.');
      return;
    }

    try {
      const response = await baseApi.createPopupType({ label });
      const createdName = response?.data?.name || label;
      const refreshed = await baseApi.fetchPopupTypes();
      setPopupTypes(refreshed?.data || []);
      setPopupType(createdName);
      setNewPopupType('');
      setPopupTypeError('');
    } catch (error) {
      setPopupTypeError(error?.response?.data?.message || 'Không tạo được loại popup mới.');
    }
  };

  const validate = () => {
    const errors = {};
    const plainContent = inputValue
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

    if (!bodyForm.title) errors.title = 'Vui lòng nhập tiêu đề popup.';
    if (!plainContent) errors.content = 'Vui lòng nhập nội dung popup.';
    if (countryTargeting.mode === 'include' && countryTargeting.countryCodes.length === 0) {
      setCountryTargetingError('Chọn ít nhất một quốc gia hoặc chuyển sang “Tất cả quốc gia”.');
    } else {
      setCountryTargetingError('');
    }

    if (startDateCheck && endDateCheck) {
      const startMoment = moment(
        `${startTime.startDate} ${startTime.startTime}`,
        ['MMM DD YYYY HH:mm', moment.ISO_8601],
        true
      );
      const endMoment = moment(
        `${endTime.endDate} ${endTime.endTime}`,
        ['MMM DD YYYY HH:mm', moment.ISO_8601],
        true
      );
      if (startMoment.isValid() && endMoment.isValid() && !endMoment.isAfter(startMoment)) {
        errors.schedule = 'Thời gian kết thúc phải sau thời gian bắt đầu.';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0
      && !(countryTargeting.mode === 'include' && countryTargeting.countryCodes.length === 0);
  };

  const submitPopup = async (isDraft) => {
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError('');
    try {
      await baseApi.fetchCreateNewPopup({
        bodyForm: {
          ...bodyForm,
          published: !isDraft,
          isDraft
        },
        id
      });
      navigate('/admin/popup-management');
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message
        || (id ? 'Không cập nhật được popup. Vui lòng thử lại.' : 'Không tạo được popup. Vui lòng thử lại.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateScheduleDate = (prefix, value) => {
    const formattedDate = moment(value).format('MMM DD YYYY');
    if (prefix === 'start') {
      setStartTime((current) => ({ ...current, startDate: formattedDate }));
    } else {
      setEndTime((current) => ({ ...current, endDate: formattedDate }));
    }
  };

  const updateScheduleTime = (prefix, value) => {
    if (prefix === 'start') {
      setStartTime((current) => ({ ...current, startTime: value }));
    } else {
      setEndTime((current) => ({ ...current, endTime: value }));
    }
  };

  return (
    <Box className="popup-editor-page">
      <Container maxWidth="xl" className="popup-editor-container">
        <Box className="popup-editor-intro">
          <div>
            <span className="popup-editor-eyebrow">{id ? 'CHỈNH SỬA POPUP' : 'TẠO POPUP MỚI'}</span>
            <Typography component="h1" className="popup-editor-heading">{title}</Typography>
            <Typography className="popup-editor-description">{description}</Typography>
          </div>
          <div className="popup-editor-progress" aria-label="Các bước cấu hình popup">
            <span className="is-active"><b>1</b>Nội dung</span>
            <i />
            <span className="is-active"><b>2</b>Hiển thị</span>
            <i />
            <span><b>3</b>Đăng</span>
          </div>
        </Box>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <section className="popup-editor-card">
              <div className="popup-section-heading">
                <span className="popup-section-icon"><ArticleOutlinedIcon /></span>
                <div>
                  <Typography className="popup-section-title">Thông tin cơ bản</Typography>
                  <Typography className="popup-section-description">Đặt tiêu đề, đường dẫn và loại hiển thị cho popup.</Typography>
                </div>
                <span className="popup-section-step">01</span>
              </div>

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tiêu đề popup"
                    placeholder="Ví dụ: Ưu đãi chào hè dành cho khách hàng mới"
                    value={inpTitle}
                    onChange={(event) => {
                      setInpTitle(event.target.value.slice(0, 60));
                      setFieldErrors((current) => ({ ...current, title: '' }));
                    }}
                    error={Boolean(fieldErrors.title)}
                    helperText={fieldErrors.title || 'Tiêu đề ngắn gọn giúp người xem hiểu thông điệp ngay lập tức.'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <span className={`popup-character-count ${characterCount >= 58 ? 'is-limit' : ''}`}>
                            {characterCount}/60
                          </span>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="URL khi nhấp vào popup"
                    placeholder="https://example.com/chuong-trinh"
                    value={inpUrl}
                    onChange={(event) => setInpUrl(event.target.value)}
                    helperText="Có thể để trống nếu popup chỉ dùng để thông báo."
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    select
                    fullWidth
                    label="Loại popup"
                    value={popupType}
                    onChange={(event) => setPopupType(event.target.value)}
                    SelectProps={{ native: true }}
                  >
                    {popupTypes.map((type) => (
                      <option key={type._id || type.name} value={type.name}>
                        {type.label || type.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Box className="popup-type-create">
                    <TextField
                      fullWidth
                      size="small"
                      label="Tạo nhanh loại popup mới"
                      value={newPopupType}
                      onChange={(event) => setNewPopupType(event.target.value)}
                    />
                    <Button variant="outlined" onClick={handleAddPopupType}>Thêm loại</Button>
                  </Box>
                  {popupTypeError && <Typography className="popup-inline-error">{popupTypeError}</Typography>}
                </Grid>
              </Grid>
            </section>

            <section className="popup-editor-card popup-content-card">
              <div className="popup-section-heading">
                <span className="popup-section-icon"><TuneOutlinedIcon /></span>
                <div>
                  <Typography className="popup-section-title">Nội dung popup</Typography>
                  <Typography className="popup-section-description">Soạn nội dung rõ ràng, dễ đọc trên cả máy tính và điện thoại.</Typography>
                </div>
                <span className="popup-section-step">02</span>
              </div>

              {isCenterType && (
                <FormControl className="popup-content-mode">
                  <RadioGroup
                    row
                    value={contentMode}
                    onChange={(event) => setContentMode(event.target.value)}
                  >
                    <FormControlLabel value="normal" control={<Radio />} label="Trình soạn thảo" />
                    <FormControlLabel value="html" control={<Radio />} label="Xem HTML" />
                  </RadioGroup>
                </FormControl>
              )}

              <Box className="popup-ckeditor-wrap">
                {contentMode === 'normal' ? (
                  <>
                    {(!id || inputValue !== '<p></p>') && (
                      <CKEditor
                        config={configCkeditor}
                        initData={inputValue}
                        filebrowserImagePreviewText="Xem trước hình ảnh"
                        onChange={(event) => {
                          setInputValue(event.editor.getData());
                          setFieldErrors((current) => ({ ...current, content: '' }));
                        }}
                      />
                    )}
                    {fieldErrors.content && <Typography className="popup-inline-error">{fieldErrors.content}</Typography>}
                  </>
                ) : (
                  <Box className="htmlDisplay">
                    <Typography className="textHtml">{inputValue}</Typography>
                  </Box>
                )}
              </Box>
            </section>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box className="popup-settings-column">
              <section className="popup-editor-card popup-schedule-card">
                <div className="popup-section-heading popup-section-heading-compact">
                  <span className="popup-section-icon"><ScheduleOutlinedIcon /></span>
                  <div>
                    <Typography className="popup-section-title">Lịch hiển thị</Typography>
                    <Typography className="popup-section-description">Chủ động bật hoặc tắt từng mốc thời gian.</Typography>
                  </div>
                </div>

                <ScheduleBlock
                  title="Bắt đầu hiển thị"
                  enabled={startDateCheck}
                  enabledText="Theo thời gian đã chọn"
                  disabledText="Hiển thị ngay sau khi đăng"
                  schedule={startTime}
                  prefix="start"
                  disabled={settingData?.published}
                  onToggle={setStartDateCheck}
                  onTimeChange={updateScheduleTime}
                  onDateChange={updateScheduleDate}
                />
                <ScheduleBlock
                  title="Kết thúc hiển thị"
                  enabled={endDateCheck}
                  enabledText="Tự động dừng đúng lịch"
                  disabledText="Không giới hạn thời gian"
                  schedule={endTime}
                  prefix="end"
                  onToggle={setEndDateCheck}
                  onTimeChange={updateScheduleTime}
                  onDateChange={updateScheduleDate}
                />
                {fieldErrors.schedule && <Typography className="popup-inline-error">{fieldErrors.schedule}</Typography>}
              </section>

              <CountryTargeting
                mode={countryTargeting.mode}
                countryCodes={countryTargeting.countryCodes}
                onModeChange={(mode) => {
                  setCountryTargeting((current) => ({ ...current, mode }));
                  setCountryTargetingError('');
                }}
                onCountryCodesChange={(countryCodes) => {
                  setCountryTargeting((current) => ({ ...current, countryCodes }));
                  setCountryTargetingError('');
                }}
                error={countryTargetingError}
              />

              {isHeaderType && (
                <section className="popup-editor-card popup-color-card">
                  <div className="popup-section-heading popup-section-heading-compact">
                    <span className="popup-section-icon"><PaletteOutlinedIcon /></span>
                    <div>
                      <Typography className="popup-section-title">Màu nền headline</Typography>
                      <Typography className="popup-section-description">Chọn màu đồng bộ với nhận diện thương hiệu.</Typography>
                    </div>
                  </div>
                  <Box className="popup-color-control">
                    <label className="popup-color-picker" style={{ backgroundColor: hexColor }}>
                      <input
                        type="color"
                        value={/^#[0-9a-f]{6}$/i.test(hexColor) ? hexColor : '#41B60B'}
                        onChange={(event) => setHexColor(event.target.value.toUpperCase())}
                        aria-label="Chọn màu nền headline"
                      />
                    </label>
                    <TextField
                      fullWidth
                      size="small"
                      label="Mã màu"
                      value={hexColor}
                      onChange={(event) => setHexColor(event.target.value)}
                    />
                  </Box>
                </section>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box className="popup-action-bar">
        <Container maxWidth="xl" className="popup-action-inner">
          <div>
            <strong>{id ? 'Cập nhật popup' : 'Sẵn sàng hoàn tất?'}</strong>
            <span className={submitError ? 'is-error' : ''}>
              {submitError || 'Bạn có thể lưu bản nháp để tiếp tục chỉnh sửa sau.'}
            </span>
          </div>
          <Box className="popup-action-buttons">
            <Button className="draft-btn" onClick={() => submitPopup(true)} disabled={isSubmitting}>
              {isSubmitting ? 'Đang lưu...' : 'Lưu bản nháp'}
            </Button>
            <Button className="public-btn" onClick={() => submitPopup(false)} disabled={isSubmitting}>
              {isSubmitting ? 'Đang lưu...' : (id ? 'Lưu thay đổi' : 'Đăng popup')}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const ScheduleBlock = ({
  title,
  enabled,
  enabledText,
  disabledText,
  schedule,
  prefix,
  disabled,
  onToggle,
  onTimeChange,
  onDateChange
}) => (
  <div className="popup-schedule-block">
    <div className="popup-schedule-header">
      <div>
        <strong>{title}</strong>
        <span>{enabled ? enabledText : disabledText}</span>
      </div>
      <Switch
        checked={enabled}
        onChange={(event) => onToggle(event.target.checked)}
        inputProps={{ 'aria-label': title }}
      />
    </div>
    {enabled && (
      <Grid container spacing={1.5}>
        <Grid item xs={5}>
          <TextField
            fullWidth
            type="time"
            size="small"
            label="Giờ"
            disabled={disabled}
            value={schedule?.[`${prefix}Time`] || moment().format('HH:mm')}
            onChange={(event) => onTimeChange(prefix, event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />
        </Grid>
        <Grid item xs={7}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disablePast
              value={schedule?.[`${prefix}Date`] || moment()}
              onChange={(value) => onDateChange(prefix, value)}
              renderInput={(props) => (
                <TextField {...props} fullWidth size="small" label="Ngày" helperText="" />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    )}
  </div>
);

export default CkeditorComp;
